import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Download, Lightbulb, Loader2, Plus, Save, ShieldCheck, Trash2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TEMPLATES, TemplateRenderer } from "@/components/resume/ResumeTemplates";
import {
  JOB_TITLES,
  LANGUAGE_OPTIONS,
  OBJECTIVE_CATEGORIES,
  QUALIFICATIONS,
  RESPONSIBILITY_SUGGESTIONS,
  SKILL_CATEGORIES,
} from "@/lib/resumeSuggestions";

const EMPTY = {
  template: "classic",
  personal: {},
  objective: "",
  education: [],
  experience: [],
  skills: [],
  languages: [],
};

const SuggestionChip = ({ children, onClick, active, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
      active
        ? "bg-primary text-primary-foreground border-primary"
        : "bg-white text-muted-foreground border-border hover:text-foreground hover:border-primary/40"
    }`}
  >
    {children}
  </button>
);

const TagInput = ({ items, onChange, placeholder, testId }) => {
  const [value, setValue] = useState("");
  const add = () => {
    const v = value.trim();
    if (v && !items.includes(v)) onChange([...items, v]);
    setValue("");
  };
  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <Badge key={item} variant="secondary" className="rounded-full gap-1.5 pr-1.5">
            {item}
            <button onClick={() => onChange(items.filter((i) => i !== item))} className="hover:text-destructive" aria-label={`Remove ${item}`}>
              <Trash2 className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          data-testid={testId}
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        />
        <Button type="button" variant="outline" className="rounded-lg bg-white shrink-0" onClick={add} data-testid={testId ? `${testId}-add` : undefined}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const RepeatCard = ({ children, onRemove, testId }) => (
  <div className="rounded-lg border border-border p-4 relative">
    <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-7 w-7 text-muted-foreground hover:text-destructive" onClick={onRemove} data-testid={testId} aria-label="Remove entry">
      <Trash2 className="h-3.5 w-3.5" />
    </Button>
    {children}
  </div>
);

export const ResumeBuilderCore = () => {
  const { user, login } = useAuth();
  const isMember = user?.role === "member";
  const [resume, setResume] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [guest, setGuest] = useState({ name: "", mobile: "", otp: "", devOtp: "", step: 1 });
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  const [objectiveCat, setObjectiveCat] = useState("");
  const [skillCat, setSkillCat] = useState(SKILL_CATEGORIES[0].key);
  const [skillSearch, setSkillSearch] = useState("");
  const guestLoaded = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (isMember) {
      api.get("/resumes/me").then((res) => setResume({ ...EMPTY, ...res.data })).catch(() => {}).finally(() => setLoading(false));
    } else {
      try {
        const saved = JSON.parse(localStorage.getItem("ut_guest_resume") || "null");
        if (saved) setResume({ ...EMPTY, ...saved });
      } catch { /* ignore */ }
      guestLoaded.current = true;
      setLoading(false);
    }
  }, [isMember]);

  // persist guest work locally
  useEffect(() => {
    if (!isMember && guestLoaded.current) {
      localStorage.setItem("ut_guest_resume", JSON.stringify(resume));
    }
  }, [resume, isMember]);

  const set = useCallback((key, value) => setResume((r) => ({ ...r, [key]: value })), []);
  const setPersonal = (field, value) => setResume((r) => ({ ...r, personal: { ...(r.personal || {}), [field]: value } }));
  const updateItem = (key, idx, field, value) =>
    setResume((r) => ({ ...r, [key]: r[key].map((x, i) => (i === idx ? { ...x, [field]: value } : x)) }));
  const removeItem = (key, idx) => setResume((r) => ({ ...r, [key]: r[key].filter((_, i) => i !== idx) }));
  const addItem = (key, item = {}) => setResume((r) => ({ ...r, [key]: [...r[key], item] }));

  const addSkill = (skill) => {
    if (!resume.skills.includes(skill)) set("skills", [...resume.skills, skill]);
  };
  const toggleLanguage = (lang) => {
    set("languages", resume.languages.includes(lang) ? resume.languages.filter((l) => l !== lang) : [...resume.languages, lang]);
  };
  const appendResponsibility = (idx, line) => {
    const current = resume.experience[idx]?.description || "";
    if (current.includes(line)) return;
    updateItem("experience", idx, "description", current ? `${current}\n• ${line}` : `• ${line}`);
  };

  const persistPayload = () => ({
    template: resume.template,
    personal: resume.personal,
    objective: resume.objective,
    education: resume.education.filter((e) => (e.degree || "").trim() || (e.institution || "").trim()),
    experience: resume.experience.filter((e) => (e.title || "").trim() || (e.company || "").trim()),
    skills: resume.skills,
    languages: resume.languages,
  });

  const downloadPdf = () => {
    // The saved PDF filename comes from the document title — use the candidate's name.
    const prevTitle = document.title;
    const candidate = (resume.personal?.name || "My").trim();
    document.title = `${candidate} Resume`;
    const restore = () => {
      document.title = prevTitle;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    window.print();
    setTimeout(restore, 3000); // fallback for browsers that don't fire afterprint
  };

  const save = async () => {
    if (!isMember) {
      setSaveOpen(true);
      return;
    }
    setSaving(true);
    try {
      const res = await api.put("/resumes/me", persistPayload());
      setResume({ ...EMPTY, ...res.data });
      toast.success("Resume saved");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const guestSendOtp = async () => {
    if (guest.name.trim().length < 2) return toast.error("Please enter your full name");
    if (!/^[0-9]{10}$/.test(guest.mobile)) return toast.error("Please enter a valid 10-digit mobile number");
    setSaving(true);
    try {
      const res = await api.post("/auth/request-otp", { mobile: guest.mobile });
      setGuest((g) => ({ ...g, devOtp: res.data.dev_otp, step: 2 }));
      toast.success("OTP sent to your mobile number");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const guestVerifyAndSave = async () => {
    if (guest.otp.length !== 6) return toast.error("Please enter the 6-digit OTP");
    setSaving(true);
    try {
      const reg = await api.post("/auth/register-member", { name: guest.name.trim(), mobile: guest.mobile, otp: guest.otp });
      login(reg.data.token, reg.data.user);
      const payload = persistPayload();
      if (!payload.personal?.name) payload.personal = { ...(payload.personal || {}), name: guest.name.trim(), mobile: guest.mobile };
      const res = await api.put("/resumes/me", payload);
      setResume({ ...EMPTY, ...res.data });
      localStorage.removeItem("ut_guest_resume");
      setSaveOpen(false);
      toast.success("Resume saved to your account");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-16 text-center text-sm text-muted-foreground">Loading resume builder...</div>;
  }

  const activeObjectiveCat = OBJECTIVE_CATEGORIES.find((c) => c.key === objectiveCat);
  const activeSkills = SKILL_CATEGORIES.find((c) => c.key === skillCat)?.skills || [];
  const searchedSkills = skillSearch.trim()
    ? SKILL_CATEGORIES.flatMap((c) => c.skills).filter((s, i, arr) => arr.indexOf(s) === i && s.toLowerCase().includes(skillSearch.toLowerCase()))
    : activeSkills;

  const editor = (
    <div className="space-y-4">
      {/* Template picker */}
      <div className="rounded-xl border border-border bg-white p-4">
        <p className="text-sm font-medium mb-2.5">Resume template</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="resume-template-picker">
          {TEMPLATES.map((t) => (
            <button
              key={t.key}
              onClick={() => set("template", t.key)}
              data-testid={`resume-template-${t.key}`}
              className={`rounded-lg border px-2 py-2 text-xs font-medium transition-colors ${
                resume.template === t.key ? "border-primary bg-primary/5 text-primary" : "border-border bg-white hover:bg-muted"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <Accordion type="multiple" defaultValue={["personal"]} className="space-y-3">
        <AccordionItem value="personal" className="rounded-xl border border-border bg-white px-4">
          <AccordionTrigger className="text-sm font-medium hover:no-underline" data-testid="resume-section-personal">Personal Information</AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Full name</Label>
                <Input data-testid="resume-name-input" value={resume.personal?.name || ""} onChange={(e) => setPersonal("name", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Mobile</Label>
                <Input data-testid="resume-mobile-input" value={resume.personal?.mobile || ""} disabled={isMember} className={isMember ? "bg-muted font-mono" : ""} onChange={(e) => setPersonal("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))} />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input data-testid="resume-email-input" type="email" value={resume.personal?.email || ""} onChange={(e) => setPersonal("email", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>City</Label>
                <Input data-testid="resume-city-input" value={resume.personal?.city || ""} onChange={(e) => setPersonal("city", e.target.value)} />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="objective" className="rounded-xl border border-border bg-white px-4">
          <AccordionTrigger className="text-sm font-medium hover:no-underline" data-testid="resume-section-objective">Career Objective</AccordionTrigger>
          <AccordionContent className="pb-4 space-y-3">
            <div>
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5"><Lightbulb className="h-3.5 w-3.5 text-amber-500" /> Pick your field to see ready-made objectives — select one and edit it, or write your own.</p>
              <div className="flex flex-wrap gap-1.5" data-testid="objective-category-chips">
                {OBJECTIVE_CATEGORIES.map((c) => (
                  <SuggestionChip key={c.key} active={objectiveCat === c.key} onClick={() => setObjectiveCat(objectiveCat === c.key ? "" : c.key)} testId={`objective-cat-${c.key}`}>
                    {c.label}
                  </SuggestionChip>
                ))}
              </div>
            </div>
            {activeObjectiveCat && (
              <div className="space-y-2" data-testid="objective-suggestions">
                {activeObjectiveCat.objectives.map((o, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { set("objective", o); toast.success("Objective added — feel free to edit it"); }}
                    data-testid={`objective-suggestion-${i}`}
                    className={`w-full text-left text-xs leading-relaxed rounded-lg border px-3 py-2.5 transition-colors ${
                      resume.objective === o ? "border-primary bg-primary/5 text-foreground" : "border-border bg-muted/40 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                    }`}
                  >
                    {o}
                  </button>
                ))}
              </div>
            )}
            <Textarea data-testid="resume-objective-input" rows={3} placeholder="Or write your own 2-3 line objective..." value={resume.objective || ""} onChange={(e) => set("objective", e.target.value)} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="education" className="rounded-xl border border-border bg-white px-4">
          <AccordionTrigger className="text-sm font-medium hover:no-underline" data-testid="resume-section-education">Education {resume.education.length > 0 && <Badge variant="secondary" className="ml-2 rounded-full">{resume.education.length}</Badge>}</AccordionTrigger>
          <AccordionContent className="pb-4 space-y-3">
            {resume.education.map((edu, i) => (
              <RepeatCard key={i} onRemove={() => removeItem("education", i)} testId={`education-remove-${i}`}>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label>Qualification</Label>
                    <Input placeholder="e.g. B.Com" value={edu.degree || ""} onChange={(e) => updateItem("education", i, "degree", e.target.value)} />
                    <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`qualification-chips-${i}`}>
                      {QUALIFICATIONS.map((q) => (
                        <SuggestionChip key={q} active={edu.degree === q} onClick={() => updateItem("education", i, "degree", q)}>
                          {q}
                        </SuggestionChip>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5"><Label>Institution / Board</Label><Input value={edu.institution || ""} onChange={(e) => updateItem("education", i, "institution", e.target.value)} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Year</Label><Input placeholder="e.g. 2023" value={edu.year || ""} onChange={(e) => updateItem("education", i, "year", e.target.value)} /></div>
                    <div className="space-y-1.5"><Label>Grade / %</Label><Input placeholder="optional" value={edu.grade || ""} onChange={(e) => updateItem("education", i, "grade", e.target.value)} /></div>
                  </div>
                </div>
              </RepeatCard>
            ))}
            <Button variant="outline" size="sm" className="rounded-lg bg-white" onClick={() => addItem("education")} data-testid="education-add-button">
              <Plus className="h-4 w-4 mr-1.5" /> Add education
            </Button>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="experience" className="rounded-xl border border-border bg-white px-4">
          <AccordionTrigger className="text-sm font-medium hover:no-underline" data-testid="resume-section-experience">Experience {resume.experience.length > 0 && <Badge variant="secondary" className="ml-2 rounded-full">{resume.experience.length}</Badge>}</AccordionTrigger>
          <AccordionContent className="pb-4 space-y-3">
            <p className="text-xs text-muted-foreground">Freshers can leave this empty — it will be hidden automatically.</p>
            {resume.experience.map((exp, i) => {
              const respSuggestions = RESPONSIBILITY_SUGGESTIONS[exp.title] || RESPONSIBILITY_SUGGESTIONS._general;
              return (
                <RepeatCard key={i} onRemove={() => removeItem("experience", i)} testId={`experience-remove-${i}`}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label>Job title</Label>
                      <Input value={exp.title || ""} onChange={(e) => updateItem("experience", i, "title", e.target.value)} />
                      <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`job-title-chips-${i}`}>
                        {JOB_TITLES.map((t) => (
                          <SuggestionChip key={t} active={exp.title === t} onClick={() => updateItem("experience", i, "title", t)}>
                            {t}
                          </SuggestionChip>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-1.5"><Label>Company</Label><Input value={exp.company || ""} onChange={(e) => updateItem("experience", i, "company", e.target.value)} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label>Start</Label><Input placeholder="Jan 2023" value={exp.start || ""} onChange={(e) => updateItem("experience", i, "start", e.target.value)} /></div>
                      <div className="space-y-1.5"><Label>End</Label><Input disabled={exp.current} placeholder={exp.current ? "Present" : "Dec 2024"} value={exp.end || ""} onChange={(e) => updateItem("experience", i, "end", e.target.value)} /></div>
                    </div>
                    <div className="flex items-center gap-2 sm:col-span-2">
                      <Checkbox id={`current-${i}`} checked={!!exp.current} onCheckedChange={(v) => updateItem("experience", i, "current", !!v)} />
                      <Label htmlFor={`current-${i}`} className="text-sm font-normal">I currently work here</Label>
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label>Responsibilities / Description</Label>
                      <Textarea rows={3} value={exp.description || ""} onChange={(e) => updateItem("experience", i, "description", e.target.value)} />
                      <div className="pt-1">
                        <p className="text-[11px] text-muted-foreground mb-1.5 flex items-center gap-1"><Lightbulb className="h-3 w-3 text-amber-500" /> Click to add, then edit freely:</p>
                        <div className="flex flex-wrap gap-1.5" data-testid={`responsibility-chips-${i}`}>
                          {respSuggestions.map((rline) => (
                            <SuggestionChip key={rline} onClick={() => appendResponsibility(i, rline)}>
                              + {rline.length > 44 ? rline.slice(0, 44) + "…" : rline}
                            </SuggestionChip>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </RepeatCard>
              );
            })}
            <Button variant="outline" size="sm" className="rounded-lg bg-white" onClick={() => addItem("experience")} data-testid="experience-add-button">
              <Plus className="h-4 w-4 mr-1.5" /> Add experience
            </Button>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="skills" className="rounded-xl border border-border bg-white px-4">
          <AccordionTrigger className="text-sm font-medium hover:no-underline" data-testid="resume-section-skills">Skills {resume.skills.length > 0 && <Badge variant="secondary" className="ml-2 rounded-full">{resume.skills.length}</Badge>}</AccordionTrigger>
          <AccordionContent className="pb-4 space-y-3">
            <div className="space-y-2">
              <Input
                data-testid="skill-search-input"
                placeholder="Search skill suggestions..."
                value={skillSearch}
                onChange={(e) => setSkillSearch(e.target.value)}
              />
              {!skillSearch.trim() && (
                <div className="flex flex-wrap gap-1.5" data-testid="skill-category-chips">
                  {SKILL_CATEGORIES.map((c) => (
                    <SuggestionChip key={c.key} active={skillCat === c.key} onClick={() => setSkillCat(c.key)} testId={`skill-cat-${c.key}`}>
                      {c.label}
                    </SuggestionChip>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5" data-testid="skill-suggestions">
                {searchedSkills.filter((s) => !resume.skills.includes(s)).map((s) => (
                  <SuggestionChip key={s} onClick={() => addSkill(s)}>+ {s}</SuggestionChip>
                ))}
                {searchedSkills.filter((s) => !resume.skills.includes(s)).length === 0 && (
                  <p className="text-xs text-muted-foreground">No more suggestions — add a custom skill below.</p>
                )}
              </div>
            </div>
            <TagInput items={resume.skills} onChange={(v) => set("skills", v)} placeholder="Add a custom skill and press Enter" testId="skill-input" />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="languages" className="rounded-xl border border-border bg-white px-4">
          <AccordionTrigger className="text-sm font-medium hover:no-underline" data-testid="resume-section-languages">Languages {resume.languages.length > 0 && <Badge variant="secondary" className="ml-2 rounded-full">{resume.languages.length}</Badge>}</AccordionTrigger>
          <AccordionContent className="pb-4 space-y-3">
            <div className="flex flex-wrap gap-1.5" data-testid="language-chips">
              {LANGUAGE_OPTIONS.map((l) => (
                <SuggestionChip key={l} active={resume.languages.includes(l)} onClick={() => toggleLanguage(l)} testId={`language-chip-${l.toLowerCase()}`}>
                  {l}
                </SuggestionChip>
              ))}
            </div>
            <TagInput items={resume.languages.filter((l) => !LANGUAGE_OPTIONS.includes(l))} onChange={(custom) => set("languages", [...resume.languages.filter((l) => LANGUAGE_OPTIONS.includes(l)), ...custom])} placeholder="Add another language" testId="language-input" />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex gap-3 pt-1">
        <Button onClick={save} disabled={saving} className="rounded-lg" data-testid="resume-save-button">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} {isMember ? "Save Resume" : "Save to My Account"}
        </Button>
        <Button onClick={downloadPdf} variant="outline" className="rounded-lg bg-white" data-testid="resume-download-pdf-button">
          <Download className="h-4 w-4 mr-2" /> Download PDF
        </Button>
      </div>
    </div>
  );

  const preview = (
    <div className="bg-muted/40 border border-border rounded-xl p-3 sm:p-5 overflow-auto">
      <div className="a4-paper mx-auto overflow-hidden" data-testid="resume-a4-preview">
        <TemplateRenderer resume={resume} />
      </div>
    </div>
  );

  // Print-only copy rendered directly under <body> via portal:
  // always in the DOM (works on mobile even when the preview tab is closed),
  // and isolated from the app layout so the PDF never gets extra blank pages.
  const printRoot = createPortal(
    <div id="resume-print-root" aria-hidden="true">
      <TemplateRenderer resume={resume} />
    </div>,
    document.body
  );

  return (
    <div className="space-y-6">
      {printRoot}
      {isDesktop ? (
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          {editor}
          {preview}
        </div>
      ) : (
        <Tabs defaultValue="editor">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="editor" data-testid="resume-tab-editor">Editor</TabsTrigger>
            <TabsTrigger value="preview" data-testid="resume-tab-preview">Live Preview</TabsTrigger>
          </TabsList>
          <TabsContent value="editor" className="mt-4">{editor}</TabsContent>
          <TabsContent value="preview" className="mt-4">{preview}</TabsContent>
        </Tabs>
      )}

      {/* Guest save via OTP */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="sm:max-w-sm rounded-xl" data-testid="resume-guest-save-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Save your resume</DialogTitle>
            <DialogDescription>Verify your mobile number to save your resume to your free account.</DialogDescription>
          </DialogHeader>
          {guest.step === 1 ? (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Full name</Label>
                <Input data-testid="guest-save-name-input" value={guest.name} onChange={(e) => setGuest((g) => ({ ...g, name: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Mobile number</Label>
                <Input data-testid="guest-save-mobile-input" inputMode="numeric" maxLength={10} value={guest.mobile} onChange={(e) => setGuest((g) => ({ ...g, mobile: e.target.value.replace(/\D/g, "") }))} />
              </div>
              <Button onClick={guestSendOtp} disabled={saving} className="w-full rounded-lg" data-testid="guest-save-send-otp-button">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Send OTP
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-3">
                <InputOTP maxLength={6} value={guest.otp} onChange={(v) => setGuest((g) => ({ ...g, otp: v }))} data-testid="guest-save-otp-input">
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                  </InputOTPGroup>
                </InputOTP>
                {guest.devOtp && (
                  <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-1.5" data-testid="guest-save-dev-otp-chip">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                    <span className="text-xs text-emerald-700">Dev mode OTP:</span>
                    <span className="font-mono text-sm font-semibold text-emerald-800">{guest.devOtp}</span>
                  </div>
                )}
              </div>
              <Button onClick={guestVerifyAndSave} disabled={saving} className="w-full rounded-lg" data-testid="guest-save-verify-button">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Verify & Save Resume
              </Button>
              <Button variant="ghost" onClick={() => setGuest((g) => ({ ...g, step: 1, otp: "" }))} className="w-full rounded-lg">Back</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
